import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Modal, Button, FormControl, ControlLabel } from 'react-bootstrap';
import moment from 'moment';
import FormInput from '../../components/FormInput';
import * as adminActionCreator from '../../actions/admin';
import { RPM_CONSENT_TYPE_OPTIONS } from '../../constants/rpmEnrollmentMocks';
import { getRpmDeviceClass, getRpmDeviceTypeLabel } from '../../constants/other';
import {
    buildRpmEnrollPayload,
    formatValidatedAddressLabel,
    normalizeValidatedAddress,
} from '../../utils/rpmEnrollmentHelpers';
import {
    fetchRpmDevices,
    validateRpmShippingAddress,
} from '../../services/rpmEnrollmentService';

const STEP_LABELS = ['Device', 'Shipping', 'Confirm'];

const boxStyle = {
    background: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: 4,
    padding: 12,
    marginBottom: 16,
};

const selectedDeviceBoxStyle = {
    background: 'rgb(240,249,243)',
    border: '1px solid rgb(109,173,130)',
    borderRadius: 4,
    padding: 12,
    marginTop: 12,
};

class RpmAddonEnrollmentModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = this.getInitialState(props.patient);
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.show && !this.props.show) {
            this.setState(this.getInitialState(nextProps.patient));
        }
    }

    getInitialState(patient) {
        const originalMobile = patient && patient.mobile ? patient.mobile : '';
        return {
            step: 1,
            connectionType: '',
            devices: [],
            devicesLoading: false,
            devicesError: '',
            selectedDeviceId: '',
            originalMobile,
            mobile: originalMobile,
            shipping: {
                address1: patient && patient.address1 ? patient.address1 : '',
                address2: patient && patient.address2 ? patient.address2 : '',
                city: patient && patient.city ? patient.city : '',
                state: patient && patient.state ? patient.state : '',
                zip_code: patient && patient.zip_code ? patient.zip_code : '',
            },
            addressVerified: false,
            addressValidationMessage: '',
            addressValidationError: '',
            validatingAddress: false,
            validatedAddresses: [],
            selectedValidatedAddressIndex: null,
            consentChecked: false,
            consentType: '',
            submitting: false,
            submitError: '',
        };
    }

    getSelectedDevice() {
        return this.state.devices.find((device) => device.id === this.state.selectedDeviceId);
    }

    getRpmDiagnosisDisplay() {
        const { patient } = this.props;
        if (!patient) {
            return '—';
        }
        if (patient.rpm_icd10code) {
            return [patient.rpm_icd10code, patient.rpm_icd10code_desc].filter(Boolean).join(' — ');
        }
        return '—';
    }

    getDeviceTypeLabel() {
        const { patient, rpmDeviceTypes } = this.props;
        return getRpmDeviceTypeLabel(rpmDeviceTypes, patient && patient.rpm_device_type_id) || '—';
    }

    getConnectionLabel() {
        if (this.state.connectionType === 'bluetooth') {
            return 'Bluetooth';
        }
        if (this.state.connectionType === 'cellular') {
            return 'Cellular';
        }
        return '';
    }

    getMobileFormValue() {
        return this.mobileInputRef && this.mobileInputRef.getValue
            ? this.mobileInputRef.getValue()
            : this.state.mobile;
    }

    syncMobileState() {
        const mobile = this.getMobileFormValue();
        this.setState({ mobile });
        return mobile;
    }

    isBluetoothStep1Complete() {
        if (this.state.connectionType !== 'bluetooth') {
            return true;
        }
        if (!this.mobileInputRef || !this.mobileInputRef.validate()) {
            return false;
        }
        return !!this.getMobileFormValue();
    }

    onMobileBlur() {
        this.syncMobileState();
    }

    onConnectionTypeChange(connectionType) {
        const { patient, rpmDeviceTypes } = this.props;
        const deviceClass = getRpmDeviceClass(rpmDeviceTypes, patient && patient.rpm_device_type_id);
        const originalMobile = patient && patient.mobile ? patient.mobile : '';
        const mobileState = {
            originalMobile,
            mobile: originalMobile,
        };

        if (!deviceClass) {
            this.setState({
                connectionType,
                devices: [],
                selectedDeviceId: '',
                devicesError: 'Device type is not available for this patient.',
                ...mobileState,
            });
            return;
        }

        this.setState({
            connectionType,
            selectedDeviceId: '',
            devices: [],
            devicesLoading: true,
            devicesError: '',
            ...mobileState,
        }, () => {
            fetchRpmDevices(deviceClass, connectionType)
                .then((devices) => {
                    this.setState({
                        devices,
                        devicesLoading: false,
                        devicesError: devices.length ? '' : 'No devices available for this connection type.',
                    });
                })
                .catch(() => {
                    this.setState({
                        devicesLoading: false,
                        devicesError: 'Failed to load devices. Please try again.',
                    });
                });
        });
    }

    onValidateAddress() {
        this.setState({
            validatingAddress: true,
            addressValidationError: '',
            addressValidationMessage: '',
            addressVerified: false,
            validatedAddresses: [],
            selectedValidatedAddressIndex: null,
        });
        validateRpmShippingAddress(this.state.shipping)
            .then((result) => {
                if (result.status === 'verified') {
                    this.setState({
                        validatingAddress: false,
                        addressVerified: true,
                        shipping: result.shipping,
                        validatedAddresses: [],
                        selectedValidatedAddressIndex: null,
                        addressValidationMessage: result.message,
                        addressValidationError: '',
                    });
                } else if (result.status === 'selection_required') {
                    this.setState({
                        validatingAddress: false,
                        addressVerified: false,
                        validatedAddresses: result.addresses,
                        selectedValidatedAddressIndex: null,
                        addressValidationMessage: result.message,
                        addressValidationError: '',
                    });
                } else {
                    this.setState({
                        validatingAddress: false,
                        addressVerified: false,
                        validatedAddresses: [],
                        selectedValidatedAddressIndex: null,
                        addressValidationError: result.error,
                        addressValidationMessage: '',
                    });
                }
            })
            .catch(() => {
                this.setState({
                    validatingAddress: false,
                    addressVerified: false,
                    validatedAddresses: [],
                    selectedValidatedAddressIndex: null,
                    addressValidationError: 'Address validation failed. Please try again.',
                    addressValidationMessage: '',
                });
            });
    }

    onSelectValidatedAddress(index) {
        const address = this.state.validatedAddresses[index];
        if (!address) {
            return;
        }
        this.setState({
            selectedValidatedAddressIndex: index,
            shipping: normalizeValidatedAddress(address),
            addressVerified: true,
            addressValidationMessage: 'Address verified. Deliverable — ready to enroll.',
            addressValidationError: '',
        });
    }

    onShippingChange(field, value) {
        this.setState({
            shipping: {
                ...this.state.shipping,
                [field]: value,
            },
            addressVerified: false,
            addressValidationError: '',
            addressValidationMessage: '',
            validatedAddresses: [],
            selectedValidatedAddressIndex: null,
        });
    }

    goToStep(step) {
        this.setState({ step, submitError: '' });
    }

    onContinueFromStep1() {
        if (!this.state.selectedDeviceId) {
            return;
        }
        if (this.state.connectionType === 'bluetooth') {
            if (!this.isBluetoothStep1Complete()) {
                return;
            }
            const mobile = this.getMobileFormValue();
            this.setState({ mobile }, () => this.goToStep(2));
            return;
        }
        this.goToStep(2);
    }

    onContinueFromStep2() {
        if (this.state.addressVerified) {
            this.goToStep(3);
        }
    }

    onSubmitEnrollment() {
        const { patient, adminActions, onEnrollComplete } = this.props;
        const { connectionType, shipping } = this.state;
        const selectedDevice = this.getSelectedDevice();
        const consentOption = RPM_CONSENT_TYPE_OPTIONS.find((option) => option.value === this.state.consentType);
        const mobile = connectionType === 'bluetooth' ? this.syncMobileState() : null;
        const payload = buildRpmEnrollPayload({
            consentType: this.state.consentType,
            selectedDevice,
            shipping,
            connectionType,
            mobile,
        });

        this.setState({ submitting: true, submitError: '' });
        adminActions.enrollRpmAddonPatient(patient.id, payload)
            .then((result) => {
                this.setState({ submitting: false });
                onEnrollComplete({
                    ...result,
                    participantId: patient.rpm_validic_participant_id || `RPM-${patient.id}`,
                    enrolledTs: moment().format('MMM D, YYYY'),
                    deviceDescription: selectedDevice.description,
                    workItemText: `RPM Add-On consent obtained (${consentOption ? consentOption.label : this.state.consentType}) and enrollment confirmed on ${moment().format('MMM D, YYYY')}. Device: ${selectedDevice.description}.`,
                    connectionLabel: this.getConnectionLabel(),
                    diagnosisDisplay: this.getRpmDiagnosisDisplay(),
                    consentType: this.state.consentType,
                    consentTypeLabel: consentOption ? consentOption.label : this.state.consentType,
                    shipping: { ...shipping },
                    programId: selectedDevice.programId,
                    deviceId: selectedDevice.id,
                });
            })
            .catch(() => {
                this.setState({
                    submitting: false,
                    submitError: 'Enrollment failed. Please try again.',
                });
            });
    }

    renderStepper() {
        const { step } = this.state;
        return (
            <div style={{ display: 'flex', alignItems: 'center', margin: '16px 0 24px' }}>
                {STEP_LABELS.map((label, index) => {
                    const stepNumber = index + 1;
                    const isComplete = step > stepNumber;
                    const isActive = step === stepNumber;
                    const circleStyle = {
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 600,
                        marginRight: 8,
                        background: isComplete ? '#5cb85c' : isActive ? '#337ab7' : '#ccc',
                        color: '#fff',
                    };
                    return (
                        <React.Fragment key={label}>
                            <div style={{ display: 'flex', alignItems: 'center', color: isActive ? '#337ab7' : '#666' }}>
                                <span style={circleStyle}>{isComplete ? '✓' : stepNumber}</span>
                                <span style={{ fontWeight: isActive ? 600 : 400 }}>{label}</span>
                            </div>
                            {index < STEP_LABELS.length - 1 &&
                                <div style={{
                                    flex: 1,
                                    height: 2,
                                    background: step > stepNumber ? '#5cb85c' : '#ddd',
                                    margin: '0 12px',
                                }} />
                            }
                        </React.Fragment>
                    );
                })}
            </div>
        );
    }

    renderPatientHeader() {
        const { patient } = this.props;
        if (!patient) {
            return null;
        }
        const age = patient.date_of_birth ? moment().diff(moment(patient.date_of_birth), 'years') : '—';
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 12,
                paddingBottom: 12,
                borderBottom: '1px solid #ddd',
                marginBottom: 8,
            }}>
                <div>
                    <div style={{ fontWeight: 600, color: '#337ab7', fontSize: 16 }}>
                        {patient.first_name} {patient.last_name}
                    </div>
                    <div style={{ fontSize: 13, color: '#666' }}>
                        {patient.date_of_birth ? moment(patient.date_of_birth).format('MM/DD/YYYY') : '—'}
                        {' · '}Age {age}
                        {' · '}CCM ID {patient.id}
                    </div>
                </div>
                <div style={{ fontSize: 13 }}>
                    <div><span style={{ fontWeight: 600 }}>Insurance 1:</span> {patient.insurance1 || 'N/A'}</div>
                    <div><span style={{ fontWeight: 600 }}>Insurance 2:</span> {patient.insurance2 || 'N/A'}</div>
                    <div style={{ fontStyle: 'italic', color: '#666', marginTop: 4 }}>
                        Confirm the patient's copay before enrolling.
                    </div>
                </div>
            </div>
        );
    }

    renderBluetoothMobileSection() {
        const originalMobile = this.state.originalMobile;

        return (
            <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
                    Confirm or update the patient's mobile phone for bluetooth device enrollment.
                </div>
                <ControlLabel>Mobile Phone</ControlLabel>
                <FormInput
                    key={`rpm-mobile-bluetooth-${originalMobile}`}
                    type="phone"
                    isRequired
                    ref={(ref) => { this.mobileInputRef = ref; }}
                    placeholder="(XXX) XXX-XXXX"
                    handleBlurEvent={this.onMobileBlur.bind(this)}
                    initialValue={originalMobile}
                    maxLength="25"
                />
            </div>
        );
    }

    renderStep1() {
        const { connectionType, devices, devicesLoading, devicesError, selectedDeviceId } = this.state;
        const selectedDevice = this.getSelectedDevice();
        const deviceTypeLabel = this.getDeviceTypeLabel();
        const connectionLabel = this.getConnectionLabel();

        return (
            <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Step 1 — select device</div>
                <div style={{ fontSize: 14, color: '#555', marginBottom: 16 }}>
                    The device type is set by the provider-approved order. Choose the connectivity option and the specific device from the Impact device catalog.
                </div>
                <div style={boxStyle}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>ORDER</div>
                    <div><span style={{ fontWeight: 600 }}>Device Type:</span> {deviceTypeLabel}</div>
                    <div><span style={{ fontWeight: 600 }}>Diagnosis:</span> RPM dx {this.getRpmDiagnosisDisplay()}</div>
                </div>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>DEVICE</div>
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>1. How will the device connect?</div>
                    <label style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 8, cursor: 'pointer' }}>
                        <input
                            type="radio"
                            name="connectionType"
                            checked={connectionType === 'bluetooth'}
                            onChange={() => this.onConnectionTypeChange('bluetooth')}
                            style={{ marginRight: 8, marginTop: 3, flexShrink: 0 }}
                        />
                        <div>
                        Bluetooth
                        <span style={{
                            marginLeft: 8,
                            background: '#5cb85c',
                            color: '#fff',
                            fontSize: 11,
                            padding: '2px 6px',
                            borderRadius: 10,
                        }}>
                            Preferred
                        </span>
                        <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                            Requires a smartphone, internet access, and a companion app.
                        </div>
                        {connectionType === 'bluetooth' && this.renderBluetoothMobileSection()}
                        </div>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                        <input
                            type="radio"
                            name="connectionType"
                            checked={connectionType === 'cellular'}
                            onChange={() => this.onConnectionTypeChange('cellular')}
                            style={{ marginRight: 8, marginTop: 3, flexShrink: 0 }}
                        />
                        <div>
                        Cellular
                        <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                            For patients without smartphones or internet — ships pre-connected.
                        </div>
                        </div>
                    </label>
                </div>
                {connectionType &&
                    <div>
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>2. Which device?</div>
                        <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
                            Showing {connectionLabel} {deviceTypeLabel} only — {devices.length} available
                        </div>
                        <FormControl
                            componentClass="select"
                            value={selectedDeviceId}
                            disabled={devicesLoading}
                            onChange={(e) => this.setState({ selectedDeviceId: e.target.value })}
                        >
                            <option value="">
                                {devicesLoading ? 'Loading devices...' : `Select a ${connectionLabel} ${deviceTypeLabel}...`}
                            </option>
                            {devices.map((device) =>
                                <option key={device.id} value={device.id}>{device.description}</option>
                            )}
                        </FormControl>
                        {devicesError &&
                            <div style={{ color: '#a94442', marginTop: 8 }}>{devicesError}</div>
                        }
                        {selectedDevice &&
                            <div style={selectedDeviceBoxStyle}>
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>SELECTED DEVICE</div>
                                <div>{selectedDevice.description} · {connectionLabel}</div>
                            </div>
                        }
                    </div>
                }
            </div>
        );
    }

    renderStep2() {
        const {
            shipping,
            addressVerified,
            addressValidationMessage,
            addressValidationError,
            validatingAddress,
            validatedAddresses,
            selectedValidatedAddressIndex,
        } = this.state;
        return (
            <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Step 2 — shipping address</div>
                <div style={{ fontSize: 14, color: '#555', marginBottom: 16 }}>
                    Pre-populated from the patient record. Override if the device should ship elsewhere, then validate the address.
                </div>
                <ControlLabel>Address Line 1</ControlLabel>
                <FormControl
                    value={shipping.address1}
                    onChange={(e) => this.onShippingChange('address1', e.target.value)}
                    style={{ marginBottom: 12 }}
                />
                <ControlLabel>Address Line 2</ControlLabel>
                <FormControl
                    value={shipping.address2}
                    placeholder="Apt, suite, unit (optional)"
                    onChange={(e) => this.onShippingChange('address2', e.target.value)}
                    style={{ marginBottom: 12 }}
                />
                <ControlLabel>City</ControlLabel>
                <FormControl
                    value={shipping.city}
                    onChange={(e) => this.onShippingChange('city', e.target.value)}
                    style={{ marginBottom: 12 }}
                />
                <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                        <ControlLabel>State</ControlLabel>
                        <FormControl
                            value={shipping.state}
                            onChange={(e) => this.onShippingChange('state', e.target.value)}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <ControlLabel>ZIP</ControlLabel>
                        <FormControl
                            value={shipping.zip_code}
                            onChange={(e) => this.onShippingChange('zip_code', e.target.value)}
                        />
                    </div>
                </div>
                <div style={{ marginTop: 16 }}>
                    <Button
                        bsStyle={addressVerified ? 'success' : 'primary'}
                        onClick={this.onValidateAddress.bind(this)}
                        disabled={validatingAddress}
                    >
                        {validatingAddress ? 'Validating...' : addressVerified ? 'Verified' : 'Validate'}
                    </Button>
                </div>
                {validatedAddresses.length > 0 &&
                    <div style={{
                        marginTop: 16,
                        padding: 12,
                        background: '#f5f5f5',
                        border: '1px solid #ddd',
                        borderRadius: 4,
                    }}>
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>
                            {addressValidationMessage || 'Select the correct shipping address'}
                        </div>
                        {validatedAddresses.map((address, index) =>
                            <label
                                key={index}
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    marginBottom: 8,
                                    cursor: 'pointer',
                                }}
                            >
                                <input
                                    type="radio"
                                    name="validatedAddress"
                                    checked={selectedValidatedAddressIndex === index}
                                    onChange={() => this.onSelectValidatedAddress(index)}
                                    style={{ marginRight: 8, marginTop: 3, flexShrink: 0 }}
                                />
                                <span>{formatValidatedAddressLabel(address)}</span>
                            </label>
                        )}
                    </div>
                }
                {addressValidationMessage && validatedAddresses.length === 0 &&
                    <div style={{
                        marginTop: 12,
                        padding: 10,
                        background: 'rgb(240,249,243)',
                        border: '1px solid rgb(109,173,130)',
                        borderRadius: 4,
                        color: '#333',
                    }}>
                        {addressValidationMessage}
                    </div>
                }
                {addressValidationError &&
                    <div style={{ marginTop: 12, color: '#a94442' }}>{addressValidationError}</div>
                }
            </div>
        );
    }

    renderStep3() {
        const { patient } = this.props;
        const { consentChecked, consentType, submitError, submitting, connectionType, mobile } = this.state;
        const selectedDevice = this.getSelectedDevice();
        const consentOption = RPM_CONSENT_TYPE_OPTIONS.find((option) => option.value === consentType);
        const { shipping } = this.state;

        return (
            <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Step 3 — confirm &amp; enroll</div>
                <div style={{ fontSize: 14, color: '#555', marginBottom: 16 }}>
                    Review, confirm consent, and submit to complete RPM enrollment.
                </div>
                <div style={boxStyle}>
                    <div><span style={{ fontWeight: 600 }}>Patient:</span> {patient.first_name} {patient.last_name} · CCM ID {patient.id}</div>
                    <div><span style={{ fontWeight: 600 }}>RPM Consent Type:</span> {consentOption ? consentOption.label : '—'}</div>
                    <div><span style={{ fontWeight: 600 }}>Device:</span> {this.getDeviceTypeLabel()} · {this.getConnectionLabel()} · {selectedDevice ? selectedDevice.description : '—'}</div>
                    <div><span style={{ fontWeight: 600 }}>RPM Diagnosis Code:</span> {this.getRpmDiagnosisDisplay()}</div>
                    {connectionType === 'bluetooth' &&
                        <div><span style={{ fontWeight: 600 }}>Mobile Phone:</span> {mobile || '—'}</div>
                    }
                    <div>
                        <span style={{ fontWeight: 600 }}>Ship To:</span>
                        {' '}{shipping.address1}{shipping.address2 ? `, ${shipping.address2}` : ''}, {shipping.city}, {shipping.state} {shipping.zip_code}
                    </div>
                </div>
                <div style={{
                    background: 'rgb(240,249,243)',
                    border: '1px solid rgb(109,173,130)',
                    borderRadius: 4,
                    padding: 16,
                }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', marginBottom: 12 }}>
                        <input
                            type="checkbox"
                            checked={consentChecked}
                            onChange={(e) => this.setState({ consentChecked: e.target.checked })}
                            style={{ marginRight: 8, marginTop: 3, flexShrink: 0 }}
                        />
                        <span>Patient has verbally consented to RPM enrollment.</span>
                    </label>
                    <ControlLabel>RPM Consent Type</ControlLabel>
                    <FormControl
                        componentClass="select"
                        value={consentType}
                        onChange={(e) => this.setState({ consentType: e.target.value })}
                    >
                        <option value="">Select consent type</option>
                        {RPM_CONSENT_TYPE_OPTIONS.map((option) =>
                            <option key={option.value} value={option.value}>{option.label}</option>
                        )}
                    </FormControl>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                        Both fields are required to enroll. Stored on the patient record as the RPM Consent Type.
                    </div>
                </div>
                {submitError && <div style={{ color: '#a94442', marginTop: 12 }}>{submitError}</div>}
                {submitting && <div style={{ marginTop: 12, color: '#666' }}>Submitting enrollment...</div>}
            </div>
        );
    }

    renderFooter() {
        const { onHide, onOpenDecline } = this.props;
        const {
            step,
            selectedDeviceId,
            connectionType,
            addressVerified,
            consentChecked,
            consentType,
            submitting,
        } = this.state;

        const step1ContinueDisabled = !selectedDeviceId || (
            connectionType === 'bluetooth' && !this.getMobileFormValue()
        );

        return (
            <div style={{
                borderTop: '1px solid #ddd',
                paddingTop: 15,
                marginTop: 10,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <div>
                    {step === 1 &&
                        <Button onClick={onHide}>Cancel</Button>
                    }
                    {step > 1 &&
                        <Button onClick={() => this.goToStep(step - 1)}>Back</Button>
                    }
                    {step === 1 &&
                        <Button
                            bsStyle="danger"
                            style={{ marginLeft: 8, background: '#fff', color: '#d9534f', borderColor: '#d9534f' }}
                            onClick={onOpenDecline}
                        >
                            Opt Out / Decline
                        </Button>
                    }
                </div>
                <div>
                    {step === 1 &&
                        <Button bsStyle="primary" disabled={step1ContinueDisabled} onClick={this.onContinueFromStep1.bind(this)}>
                            Continue
                        </Button>
                    }
                    {step === 2 &&
                        <Button bsStyle="primary" disabled={!addressVerified} onClick={this.onContinueFromStep2.bind(this)}>
                            Continue
                        </Button>
                    }
                    {step === 3 &&
                        <Button
                            bsStyle="success"
                            disabled={!consentChecked || !consentType || submitting}
                            onClick={this.onSubmitEnrollment.bind(this)}
                        >
                            Confirm &amp; Enroll
                        </Button>
                    }
                </div>
            </div>
        );
    }

    render() {
        const { show, onHide } = this.props;
        const { step } = this.state;

        return (
            <Modal bsSize="large" show={show} onHide={onHide} backdrop="static">
                <Modal.Header closeButton onHide={onHide}>
                    <Modal.Title>
                        <div className="text-align-center">RPM Add-On Enrollment</div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {this.renderPatientHeader()}
                    {this.renderStepper()}
                    {step === 1 && this.renderStep1()}
                    {step === 2 && this.renderStep2()}
                    {step === 3 && this.renderStep3()}
                    {this.renderFooter()}
                </Modal.Body>
            </Modal>
        );
    }
}

const mapDispatchToProps = (dispatch) => ({
    adminActions: bindActionCreators(adminActionCreator, dispatch),
});

export default connect(null, mapDispatchToProps)(RpmAddonEnrollmentModal);
